import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { WorkBook, WorkSheet } from 'xlsx';

interface ReviewSpreadsheetGridViewerProps {
  file: File;
  fileName?: string | null;
  /** Rendered when parsing fails — caller can substitute a text fallback. */
  fallback?: React.ReactNode;
}

const MAX_DISPLAY_ROWS = 2000;
const DEFAULT_COL_PX = 80;

export function ReviewSpreadsheetGridViewer({ file, fileName, fallback }: ReviewSpreadsheetGridViewerProps) {
  const [workbook, setWorkbook] = useState<WorkBook | null>(null);
  const [activeSheet, setActiveSheet] = useState<string | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setParseError(null);
    setWorkbook(null);
    setActiveSheet(null);

    (async () => {
      try {
        const [{ read }, buffer] = await Promise.all([import('xlsx'), file.arrayBuffer()]);
        if (cancelled) return;
        const wb = read(buffer, { type: 'array', cellDates: true });
        if (cancelled) return;
        setWorkbook(wb);
        // Pick the first sheet that actually has data — chart sheets and other
        // non-cell sheets are skipped so the user lands on something meaningful.
        const firstWithData =
          wb.SheetNames.find((n) => !!wb.Sheets[n]?.['!ref']) ?? wb.SheetNames[0] ?? null;
        setActiveSheet(firstWithData);
      } catch (err) {
        if (cancelled) return;
        setParseError(err instanceof Error ? err.message : 'Failed to parse spreadsheet');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [file]);

  if (loading) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-muted/30">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Parsing {fileName ?? 'spreadsheet'}…
        </div>
      </div>
    );
  }

  if (parseError || !workbook || !activeSheet) {
    if (fallback) return <>{fallback}</>;
    return (
      <div className="flex h-full w-full items-center justify-center bg-muted/30 p-6">
        <div className="max-w-md rounded-md border bg-background p-6 text-center text-sm">
          <p className="font-medium text-destructive">Could not parse spreadsheet</p>
          {parseError && <p className="mt-1 text-xs text-muted-foreground">{parseError}</p>}
        </div>
      </div>
    );
  }

  const showTabs = workbook.SheetNames.length > 1;

  return (
    <div className="flex h-full w-full min-w-0 flex-col bg-background">
      {fileName && (
        <div className="border-b bg-background/80 px-3 py-1.5 text-xs text-muted-foreground">
          {fileName}
        </div>
      )}
      <div className="min-h-0 min-w-0 flex-1 overflow-auto">
        <SheetGrid sheet={workbook.Sheets[activeSheet]} key={activeSheet} />
      </div>
      {showTabs && (
        <div className="flex shrink-0 items-center gap-1 overflow-x-auto border-t bg-muted/40 px-2 py-1.5">
          {workbook.SheetNames.map((name) => {
            const hasData = !!workbook.Sheets[name]?.['!ref'];
            return (
              <button
                key={name}
                type="button"
                onClick={() => setActiveSheet(name)}
                className={`shrink-0 rounded border px-2.5 py-1 text-xs font-medium transition-colors ${
                  name === activeSheet
                    ? 'border-primary bg-background text-foreground shadow-sm'
                    : hasData
                    ? 'border-transparent text-muted-foreground hover:bg-background/60'
                    : 'border-transparent text-muted-foreground/50 italic hover:bg-background/60'
                }`}
                title={hasData ? name : `${name} (no data)`}
              >
                {name}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface SheetGridProps {
  sheet: WorkSheet | undefined;
}

interface MergeAnchor {
  rowSpan: number;
  colSpan: number;
}

interface ColInfo {
  /** Original 0-based column index in the sheet. */
  index: number;
  /** Pixel width to render. */
  widthPx: number;
}

function SheetGrid({ sheet }: SheetGridProps) {
  const data = useMemo(() => {
    if (!sheet)
      return { rows: [] as number[], visibleCols: [] as ColInfo[], range: null as null | Range, mergeAnchors: new Map<string, MergeAnchor>(), coveredCells: new Set<string>(), totalRows: 0 };

    const ref = sheet['!ref'];
    if (!ref)
      return { rows: [], visibleCols: [], range: null, mergeAnchors: new Map<string, MergeAnchor>(), coveredCells: new Set<string>(), totalRows: 0 };

    const range = decodeRange(ref);
    const totalRowCount = range.e.r - range.s.r + 1;
    const lastRow = Math.min(range.e.r, range.s.r + MAX_DISPLAY_ROWS - 1);
    const rowIndices: number[] = [];
    for (let r = range.s.r; r <= lastRow; r += 1) rowIndices.push(r);

    // Hidden-column filter — `!cols[i].hidden` flags zero-width columns.
    const colMeta = (sheet['!cols'] ?? []) as Array<{ hidden?: boolean; wpx?: number; wch?: number } | undefined>;
    const visibleCols: ColInfo[] = [];
    for (let c = range.s.c; c <= range.e.c; c += 1) {
      const meta = colMeta[c];
      if (meta?.hidden) continue;
      const widthPx = meta?.wpx
        ? Math.max(40, Math.min(400, Math.round(meta.wpx)))
        : meta?.wch
        ? Math.max(40, Math.min(400, Math.round(meta.wch * 7)))
        : DEFAULT_COL_PX;
      visibleCols.push({ index: c, widthPx });
    }

    // Build merge maps. An anchor is the top-left cell of a merge; covered cells
    // are skipped during render so the anchor's rowSpan/colSpan visually subsumes them.
    const merges = (sheet['!merges'] ?? []) as Array<{ s: { r: number; c: number }; e: { r: number; c: number } }>;
    const mergeAnchors = new Map<string, MergeAnchor>();
    const coveredCells = new Set<string>();
    const visibleColSet = new Set(visibleCols.map((c) => c.index));
    for (const m of merges) {
      // colSpan/rowSpan need to count only visible columns inside the merge range.
      let visibleColCount = 0;
      for (let c = m.s.c; c <= m.e.c; c += 1) {
        if (visibleColSet.has(c)) visibleColCount += 1;
      }
      if (visibleColCount === 0) continue; // entire merge is in hidden columns
      const rowSpan = m.e.r - m.s.r + 1;
      mergeAnchors.set(cellKey(m.s.r, m.s.c), { rowSpan, colSpan: visibleColCount });
      for (let r = m.s.r; r <= m.e.r; r += 1) {
        for (let c = m.s.c; c <= m.e.c; c += 1) {
          if (r === m.s.r && c === m.s.c) continue;
          coveredCells.add(cellKey(r, c));
        }
      }
    }

    return {
      rows: rowIndices,
      visibleCols,
      range,
      mergeAnchors,
      coveredCells,
      totalRows: totalRowCount,
    };
  }, [sheet]);

  if (!sheet || data.rows.length === 0 || data.visibleCols.length === 0) {
    return (
      <div className="p-6 text-center text-xs text-muted-foreground">
        Empty sheet.
      </div>
    );
  }

  const truncated = data.totalRows > data.rows.length;

  return (
    <div className="relative">
      <table className="border-collapse text-xs" style={{ tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: 36 }} />
          {data.visibleCols.map((col) => (
            <col key={col.index} style={{ width: col.widthPx }} />
          ))}
        </colgroup>
        <thead className="sticky top-0 z-10">
          <tr>
            <th className="sticky left-0 z-20 border-r border-b bg-muted/95 px-2 py-1 text-right font-mono text-[10px] text-muted-foreground">
              {' '}
            </th>
            {data.visibleCols.map((col) => (
              <th
                key={col.index}
                className="border-r border-b bg-muted/95 px-2 py-1 text-center font-mono text-[10px] font-normal text-muted-foreground"
              >
                {colLabel(col.index)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={r} className="hover:bg-muted/40">
              <td className="sticky left-0 z-10 border-r border-b bg-muted/70 px-2 py-1 text-right font-mono text-[10px] text-muted-foreground">
                {r + 1}
              </td>
              {data.visibleCols.map((col) => {
                const key = cellKey(r, col.index);
                if (data.coveredCells.has(key)) return null;
                const anchor = data.mergeAnchors.get(key);
                const cell = sheet[encodeCell(col.index, r)];
                const text = formatCell(cell);
                return (
                  <td
                    key={col.index}
                    rowSpan={anchor?.rowSpan}
                    colSpan={anchor?.colSpan}
                    className="overflow-hidden whitespace-nowrap border-r border-b px-2 py-1 align-top"
                    title={text}
                  >
                    <div className="overflow-hidden text-ellipsis">{text}</div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {truncated && (
        <div className="border-t bg-muted/40 px-3 py-1.5 text-center text-[11px] text-muted-foreground">
          Showing first {MAX_DISPLAY_ROWS.toLocaleString()} rows of {data.totalRows.toLocaleString()}
        </div>
      )}
    </div>
  );
}

// --- minimal A1 helpers ---

interface Range {
  s: { r: number; c: number };
  e: { r: number; c: number };
}

function decodeRange(ref: string): Range {
  const [start, end = start] = ref.split(':');
  return { s: decodeCell(start), e: decodeCell(end) };
}

function decodeCell(addr: string): { r: number; c: number } {
  const m = /^([A-Z]+)(\d+)$/.exec(addr);
  if (!m) return { r: 0, c: 0 };
  const [, letters, digits] = m;
  let c = 0;
  for (let i = 0; i < letters.length; i += 1) c = c * 26 + (letters.charCodeAt(i) - 64);
  return { r: Number(digits) - 1, c: c - 1 };
}

function encodeCell(c: number, r: number): string {
  return `${colLabel(c)}${r + 1}`;
}

function colLabel(c: number): string {
  let n = c + 1;
  let s = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    s = String.fromCharCode(65 + rem) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

function cellKey(r: number, c: number): string {
  return `${r},${c}`;
}

function formatCell(cell: unknown): string {
  if (!cell || typeof cell !== 'object') return '';
  const c = cell as { w?: string; v?: unknown; t?: string };
  if (c.w != null) return c.w; // Pre-formatted text from XLSX (handles dates, numbers, etc.)
  if (c.v == null) return '';
  if (c.v instanceof Date) return c.v.toLocaleDateString();
  return String(c.v);
}
